import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Badge } from "@/components/Common";

/**
 * ManageAccessDialog
 * Admin-only dialog to manage a user's profile, role, active status,
 * permission overrides (per module x action), and reset password.
 */
export default function ManageAccessDialog({ open, onOpenChange, userId, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [target, setTarget] = useState(null); // user doc
  const [modules, setModules] = useState([]);
  const [actions, setActions] = useState(["view", "create", "edit", "delete"]);
  const [roleDefaults, setRoleDefaults] = useState({});
  const [overrides, setOverrides] = useState({}); // module -> { action: bool }
  const [newPassword, setNewPassword] = useState("");

  useEffect(() => {
    if (!open || !userId) return;
    (async () => {
      setLoading(true);
      try {
        const [meta, users, eff] = await Promise.all([
          api.get("/permissions/modules"),
          api.get("/users"),
          api.get(`/users/${userId}/effective-permissions`),
        ]);
        setModules(meta.data.modules);
        setActions(meta.data.actions);
        const u = users.data.find((x) => x.id === userId);
        setTarget(u || null);
        setRoleDefaults(eff.data.role_defaults || {});
        setOverrides(eff.data.overrides || {});
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load user");
      } finally {
        setLoading(false);
      }
    })();
  }, [open, userId]);

  if (!target) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl rounded-sm" data-testid="manage-access-dialog">
          <DialogHeader><DialogTitle>Loading...</DialogTitle></DialogHeader>
          <div className="text-sm text-slate-500 py-8">{loading ? "Loading user details..." : "User not found."}</div>
        </DialogContent>
      </Dialog>
    );
  }

  // Effective for display = roleDefaults overridden by overrides
  const isOverridden = (mod, act) => {
    const o = overrides?.[mod];
    if (!o || !(act in o)) return false;
    return o[act] !== roleDefaults?.[mod]?.[act];
  };

  const effectiveValue = (mod, act) => {
    const o = overrides?.[mod];
    if (o && act in o) return Boolean(o[act]);
    return Boolean(roleDefaults?.[mod]?.[act]);
  };

  const toggleAction = (mod, act) => {
    const current = effectiveValue(mod, act);
    const next = !current;
    const defaultVal = Boolean(roleDefaults?.[mod]?.[act]);
    setOverrides((prev) => {
      const copy = { ...prev };
      const modCopy = { ...(copy[mod] || {}) };
      if (next === defaultVal) {
        // remove override (back to default)
        delete modCopy[act];
      } else {
        modCopy[act] = next;
      }
      if (Object.keys(modCopy).length === 0) {
        delete copy[mod];
      } else {
        copy[mod] = modCopy;
      }
      return copy;
    });
  };

  const resetModuleToDefault = (mod) => {
    setOverrides((prev) => {
      const copy = { ...prev };
      delete copy[mod];
      return copy;
    });
  };

  const saveProfile = async () => {
    try {
      await api.put(`/users/${target.id}`, {
        name: target.name,
        role: target.role,
        is_active: target.is_active !== false,
      });
      toast.success("Profile updated");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const savePermissions = async () => {
    try {
      await api.put(`/users/${target.id}/permissions`, { permissions: overrides });
      toast.success("Permissions saved");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const resetPassword = async () => {
    if (!newPassword || newPassword.length < 4) {
      toast.error("Password must be at least 4 characters");
      return;
    }
    try {
      await api.post(`/users/${target.id}/reset-password`, { new_password: newPassword });
      toast.success(`Password reset for ${target.email}`);
      setNewPassword("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const reloadDefaults = async () => {
    try {
      const { data } = await api.get(`/users/${target.id}/effective-permissions`);
      setRoleDefaults(data.role_defaults);
    } catch (e) {
      // ignore — defaults will refresh on next open
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl rounded-sm max-h-[90vh] overflow-y-auto" data-testid="manage-access-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight flex items-center gap-3">
            Manage Access — {target.name}
            <span className="font-mono text-xs text-slate-500">{target.email}</span>
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="profile" className="mt-2">
          <TabsList className="rounded-sm">
            <TabsTrigger value="profile" data-testid="tab-profile">Profile & Status</TabsTrigger>
            <TabsTrigger value="permissions" data-testid="tab-permissions">Permissions</TabsTrigger>
            <TabsTrigger value="password" data-testid="tab-password">Reset Password</TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile" className="space-y-4 pt-4">
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Name</Label>
              <Input
                value={target.name}
                onChange={(e) => setTarget({ ...target, name: e.target.value })}
                className="mt-1.5 rounded-sm"
                data-testid="ma-user-name"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Role</Label>
              <Select
                value={target.role}
                onValueChange={(v) => { setTarget({ ...target, role: v }); }}
              >
                <SelectTrigger className="mt-1.5 rounded-sm" data-testid="ma-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="dispatcher">Dispatcher</SelectItem>
                  <SelectItem value="mechanic">Mechanic</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                Changing role resets the baseline permissions. Save profile, then customize overrides in the Permissions tab.
              </p>
            </div>
            <div className="flex items-center justify-between border border-slate-200 rounded-sm p-3">
              <div>
                <div className="text-sm font-semibold text-slate-900">Account Active</div>
                <div className="text-xs text-slate-500">Deactivate to block login without deleting the user.</div>
              </div>
              <Switch
                checked={target.is_active !== false}
                onCheckedChange={(v) => setTarget({ ...target, is_active: v })}
                data-testid="ma-active-toggle"
              />
            </div>
            <DialogFooter>
              <Button onClick={saveProfile} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="ma-save-profile">
                Save Profile
              </Button>
            </DialogFooter>
          </TabsContent>

          {/* Permissions Tab */}
          <TabsContent value="permissions" className="pt-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs text-slate-600">
                Toggle individual permissions to override the role default. Cells with a yellow dot are overridden.
              </div>
              <Badge tone="info" testid="ma-role-badge">Role baseline: {target.role}</Badge>
            </div>
            <div className="border border-slate-300 rounded-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="text-left p-2 font-mono text-[10px] uppercase tracking-wider text-slate-600">Module</th>
                    {actions.map((a) => (
                      <th key={a} className="p-2 font-mono text-[10px] uppercase tracking-wider text-slate-600 text-center">
                        {a}
                      </th>
                    ))}
                    <th className="p-2 text-right"></th>
                  </tr>
                </thead>
                <tbody>
                  {modules.map((m) => (
                    <tr key={m.key} className="border-t border-slate-200" data-testid={`perm-row-${m.key}`}>
                      <td className="p-2 font-semibold text-slate-800">{m.label}</td>
                      {actions.map((a) => {
                        const val = effectiveValue(m.key, a);
                        const overridden = isOverridden(m.key, a);
                        return (
                          <td key={a} className="p-2 text-center">
                            <div className="inline-flex items-center gap-1.5">
                              <Checkbox
                                checked={val}
                                onCheckedChange={() => toggleAction(m.key, a)}
                                data-testid={`perm-${m.key}-${a}`}
                              />
                              {overridden && (
                                <span
                                  className="w-1.5 h-1.5 bg-amber-500 rounded-full"
                                  title="Override applied"
                                />
                              )}
                            </div>
                          </td>
                        );
                      })}
                      <td className="p-2 text-right">
                        {overrides[m.key] && (
                          <button
                            onClick={() => resetModuleToDefault(m.key)}
                            className="text-[10px] text-slate-500 hover:text-slate-900 underline"
                            data-testid={`reset-${m.key}`}
                          >
                            reset
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setOverrides({})} className="rounded-sm" data-testid="ma-clear-overrides">
                Clear All Overrides
              </Button>
              <Button onClick={savePermissions} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="ma-save-permissions">
                Save Permissions
              </Button>
            </DialogFooter>
          </TabsContent>

          {/* Password Tab */}
          <TabsContent value="password" className="space-y-4 pt-4">
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">New Password</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="mt-1.5 rounded-sm font-mono"
                placeholder="At least 4 characters"
                data-testid="ma-new-password"
              />
              <p className="text-xs text-slate-500 mt-1">
                The user will need to log in with this new password. Share it securely.
              </p>
            </div>
            <DialogFooter>
              <Button onClick={resetPassword} className="bg-red-600 hover:bg-red-700 text-white rounded-sm" data-testid="ma-reset-password-btn">
                Reset Password
              </Button>
            </DialogFooter>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
